import { useList } from '@refinedev/core';
import { useEffect, useMemo, useState } from 'react';

type DocumentType = 'MEETING_NOTE' | 'MEETING_AGENDA' | 'SUITABILITY_REPORT';

export type DocumentVersion = {
  id: string;
  documentHistoryId: string;
  documentType: DocumentType;
  versionNumber: number;
  fileId?: number;
  markdownContent?: string;
  markdownContentWithMarkers?: string;
  createdAt: string;
  isPrimary: boolean;
};

export function useDocumentVersions(documentId?: string) {
  const [currentPrimaryId, setCurrentPrimaryId] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useList<DocumentVersion>({
    resource: `documents/${documentId}/versions`,
    queryOptions: {
      enabled: !!documentId,
    },
  });

  const versions = useMemo(() => {
    if (!data?.data?.length) return [];
    const rawVersions = data.data;

    const hasPrimarySet = rawVersions.some(v => v.isPrimary === true);
    if (hasPrimarySet) {
      return rawVersions.map(v => ({ ...v, isPrimary: v.isPrimary ?? false }));
    }

    const previousPrimaryExists =
      currentPrimaryId && rawVersions.some(v => v.id === currentPrimaryId);
    if (previousPrimaryExists) {
      return rawVersions.map(v => ({
        ...v,
        isPrimary: v.id === currentPrimaryId,
      }));
    }

    const maxVersionNumber = Math.max(...rawVersions.map(v => v.versionNumber));
    return rawVersions.map(v => ({
      ...v,
      isPrimary: v.versionNumber === maxVersionNumber,
    }));
  }, [data?.data, currentPrimaryId]);

  useEffect(() => {
    const primary = versions.find(v => v.isPrimary);
    if (primary && primary.id !== currentPrimaryId) {
      setCurrentPrimaryId(primary.id);
    }
  }, [versions, currentPrimaryId]);

  return {
    versions,
    isLoading,
    isError,
    refetch,
  };
}
